'use client';

import { useEffect, useState } from 'react';

interface CountdownTickerProps {
  /** Target date string (YYYY-MM-DD) */
  targetDate: string;
  /** Language — 'id' for Indonesian labels, 'en' for English */
  lang: string;
  /** Server-rendered fallback days (shown before hydration) */
  fallbackDays: number;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function computeTimeLeft(targetDate: string): TimeLeft | null {
  const target = new Date(targetDate + 'T00:00:00').getTime();
  const now = Date.now();
  const diff = target - now;
  if (diff <= 0) return null;

  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
  };
}

const LABELS_ID = ['Hari', 'Jam', 'Menit', 'Detik'] as const;
const LABELS_EN = ['Days', 'Hours', 'Min', 'Sec'] as const;

export function CountdownTicker({ targetDate, lang, fallbackDays }: CountdownTickerProps) {
  const isId = lang === 'id';
  const labels = isId ? LABELS_ID : LABELS_EN;

  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    // Check prefers-reduced-motion — skip ticking if enabled
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const update = () => setTimeLeft(computeTimeLeft(targetDate));
    update();
    setHydrated(true);

    if (prefersReducedMotion) return; // Show static snapshot only

    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  // Server / pre-hydration: show days-only fallback
  if (!hydrated) {
    return (
      <div className="inline-flex items-center gap-1 rounded-xl border border-border-subtle bg-surface-card px-5 py-3">
        <span className="font-heading text-3xl tabular-nums text-accent-primary">{fallbackDays}</span>
        <span className="text-sm text-text-secondary">{isId ? 'hari lagi' : (fallbackDays === 1 ? 'day left' : 'days left')}</span>
      </div>
    );
  }

  // Target date passed
  if (!timeLeft) return null;

  const segments: [number, string][] = [
    [timeLeft.days, labels[0]],
    [timeLeft.hours, labels[1]],
    [timeLeft.minutes, labels[2]],
    [timeLeft.seconds, labels[3]],
  ];

  return (
    <div className="inline-flex items-stretch gap-2 rounded-xl border border-border-subtle bg-surface-card px-4 py-3" role="timer" aria-label={isId ? 'Hitung mundur pengumuman' : 'Announcement countdown'}>
      {segments.map(([value, label], i) => (
        <div key={label} className="flex items-center gap-2">
          <div className="flex flex-col items-center">
            <span className="font-heading text-3xl tabular-nums text-accent-primary">
              {String(value).padStart(2, '0')}
            </span>
            <span className="text-xs uppercase tracking-wider text-text-tertiary">
              {label}
            </span>
          </div>
          {i < segments.length - 1 && (
            <span className="text-xl text-text-tertiary" aria-hidden="true">:</span>
          )}
        </div>
      ))}
    </div>
  );
}
